<script setup lang="ts">
import { repository } from "~/repository";
import type {
  GetSearchResultsResponse,
  SearchResult,
} from "~/repository/search.types";
import { useAsyncData } from "#app";
import { getDeviceType } from "~/utils/common";
import type { Collection } from "~/types";

definePageMeta({
  name: "pokerGO::page:search-results-page",
});

useSeoMeta({
  title: "Search | PokerGO",
  ogTitle: "Search | PokerGO",
  twitterTitle: "Search | PokerGO",
});

const route = useRoute();
const searchQuery = ref((route?.query?.q as string) || "");

const { data, pending, refresh } = await useAsyncData(
  () =>
    repository.search.getSearchResults({
      query: route.query.q as string,
      sortField: "",
      sortDirection: "",
    }),
  { lazy: true },
);

watch(
  () => route.query.q,
  () => {
    refresh();
    searchQuery.value = route.query.q as string;
  },
);

// const searchResults = ref<GetSearchResultsResponse["items"]>([]);
// const didYouMean = ref("");
// const searchResultsLoading = ref(false);

// async function handleSearch(query: string) {
//   if (query) {
//     searchResultsLoading.value = true;
//
//     didYouMean.value = "";
//     const { items, did_you_mean } = await repository.search.getSearchResults({
//       query: query,
//       sortField: "",
//       sortDirection: "",
//     });
//     console.log("did_you_mean", did_you_mean);
//     searchResults.value = items;
//     didYouMean.value = did_you_mean || "";
//     searchResultsLoading.value = false;
//   }
// }

const showDidYouMean = computed(() => {
  return (
    data.value?.did_you_mean &&
    data.value.did_you_mean.toLowerCase() != searchQuery.value.toLowerCase()
  );
});

function handleSearchResultsClick(entity: SearchResult, index: number) {
  const payload = {
    sz: `${window.screen.width}x${window.screen.height}`,
    ua: navigator.userAgent,
    sw: route.query.q as string,
    pn: "1",
    qsid: data.value?.query_session_id || "",
    qid: data.value?.query_id || "",
    cli: `${index + 1}`,
    clurl: getClurl(entity) || "",
    title: entity.title,
    dt: getDeviceType() || "",
  };

  repository.search.publishSearchTrack(payload);
}

function getClurl(entity: SearchResult) {
  let path;
  const { type, id } = entity;
  if (type === "video" || type === "vod") {
    path = "video/" + id;
  }
  if (type === "collection" || type === "playlist") {
    path = "collection/" + id;
  }
  return path;
}
</script>

<template>
  <div>
    <div class="h-20"></div>
    <div class="m-auto max-w-screen-lg px-5 py-10">
      <Banner page="search" location="header" />
      <div class="mb-10 text-4xl font-bold">Results for {{ searchQuery }}</div>
      <div v-if="pending" class="mt-20 text-center">
        <ProgressSpinner class="!h-20 !w-20" />
      </div>

      <div v-if="!pending && data && showDidYouMean" class="mb-5">
        <span class="pr-2 text-white">Did you mean?</span>
        <NuxtLink
          :to="`/search?q=${data.did_you_mean}`"
          class="text-primary-300 hover:cursor-pointer hover:underline"
        >
          {{ data.did_you_mean }}
        </NuxtLink>
      </div>

      <div v-if="!pending && data?.items?.length">
        <div class="mt-5 flex flex-col gap-5">
          <ScheduleItem
            v-for="(item, idx) in data?.items"
            :id="item.id"
            :key="`item_${idx}_${idx}`"
            :label="item.label"
            :type="item.type"
            :title="item.title"
            :description="item.description"
            :thumbnail="item.images"
            :notification-set="item.notification_set"
            :air-date="item.date"
            :video-type="item.video_type"
            :enable-notifications="false"
            :is-airing-today="item.is_airing_today"
            :is-live="item.is_live"
            :is-new-episode="item.is_new_episode"
            @click="handleSearchResultsClick(item, idx)"
          />
        </div>
      </div>

      <div
        v-if="!pending && !data?.items?.length"
        class="mt-10 text-center text-surface-300"
      >
        No search results found
      </div>
    </div>
  </div>
</template>

<style scoped></style>
